import { useMemo } from 'react';
import { promptDialog } from '../lib/dialogs';

const ADD_NEW_SENTINEL = '__add_new_neighborhood__';

interface Props {
  /** Current value. Always rendered as an option even if not in `options`. */
  value: string;
  /** Known neighborhood names (any order — deduped + sorted internally). */
  options: string[];
  /** Called with the chosen neighborhood (existing or newly typed). */
  onChange: (next: string) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
  /** Optional accessible name when no visible <label> is wired up. */
  ariaLabel?: string;
  /** Custom text for the "add new" option. Defaults to "+ Add new neighborhood…". */
  addNewLabel?: string;
}

/**
 * Combobox-style neighborhood picker: a native <select> populated with every
 * known neighborhood plus a "+ Add new neighborhood…" sentinel. Picking the
 * sentinel prompts the admin for a name and fires `onChange` with it.
 *
 * Native <select> is intentional — it inherits theming, accessibility, and
 * mobile UX from Bootstrap with zero extra dependencies. The list of known
 * neighborhoods isn't a fixed taxonomy, so we always allow free-text adds.
 */
export default function NeighborhoodSelect({
  value,
  options,
  onChange,
  id,
  className,
  disabled,
  ariaLabel,
  addNewLabel = '+ Add new neighborhood…',
}: Props) {
  // Always include the current value so a freshly-typed neighborhood that
  // hasn't propagated back into `options` yet still shows as selected.
  const merged = useMemo(() => {
    const trimmedValue = value.trim();
    const set = new Set(options.filter(Boolean));
    if (trimmedValue) set.add(trimmedValue);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [options, value]);

  const handleChange = async (next: string) => {
    if (next === ADD_NEW_SENTINEL) {
      const raw = await promptDialog({
        title: 'Add neighborhood',
        label: 'Neighborhood name',
        body: 'e.g. "Riverside", "San Marco". Saved as typed.',
      });
      if (raw == null) return; // user cancelled — keep current value
      const trimmed = raw.trim();
      if (!trimmed) return;
      // If they typed an existing one (case-insensitive), normalize to existing capitalisation.
      const existing = merged.find((n) => n.toLowerCase() === trimmed.toLowerCase());
      onChange(existing ?? trimmed);
      return;
    }
    onChange(next);
  };

  return (
    <select
      id={id}
      className={className ?? 'form-select form-select-sm'}
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => handleChange(e.target.value)}
    >
      {merged.map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
      <option value={ADD_NEW_SENTINEL}>{addNewLabel}</option>
    </select>
  );
}
